import { Link } from "react-router-dom";

/**
 * Split-screen auth layout.
 * Left: solid primary brand panel with Swiss-style typographic manifesto.
 * Right: minimal, left-aligned form surface on white.
 */
export default function AuthLayout({ children, eyebrow, title, subtitle, footer }) {
  return (
    <div className="min-h-screen bg-white flex" data-testid="auth-layout">
      {/* Brand panel */}
      <aside
        className="hidden lg:flex lg:w-[42%] flex-col justify-between bg-[#002FA7] text-white relative overflow-hidden"
        aria-hidden="true"
      >
        {/* subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* top */}
        <div className="relative p-12">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-white/60 flex items-center justify-center font-heading font-black text-lg tracking-tight">
              E
            </div>
            <span className="font-heading text-sm font-bold tracking-[0.2em] uppercase">
              Engagino
            </span>
          </div>
        </div>

        {/* middle manifesto */}
        <div className="relative px-12">
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-white/60 mb-6">
            Affiliate Operations, Refined
          </p>
          <h2 className="font-heading text-[44px] leading-[1.05] tracking-tight font-bold max-w-[90%]">
            A single source of truth for every click, conversion, and commission.
          </h2>
          <div className="mt-10 h-px w-16 bg-white/40" />
          <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
            Transparent reporting. Editable commissions. Direct payouts. Built for publishers who demand clarity.
          </p>
        </div>

        {/* bottom */}
        <div className="relative p-12 flex items-end justify-between text-[11px] tracking-[0.2em] uppercase text-white/50 font-bold">
          <span>© Engagino</span>
          <span className="font-mono">v1.0</span>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex items-center justify-center px-6 sm:px-10 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile brand mark */}
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <div className="w-9 h-9 bg-[#002FA7] text-white flex items-center justify-center font-heading font-black text-lg tracking-tight rounded-sm">
              E
            </div>
            <span className="font-heading text-sm font-bold tracking-[0.2em] uppercase text-slate-900">
              Engagino
            </span>
          </div>

          <div className="mb-8">
            {eyebrow && (
              <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-3">
                {eyebrow}
              </p>
            )}
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-none">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">{subtitle}</p>
            )}
          </div>

          {children}

          {footer && <div className="mt-8">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

export function AuthLink({ to, children, ...rest }) {
  return (
    <Link
      to={to}
      className="text-[#002FA7] font-medium hover:underline underline-offset-4"
      {...rest}
    >
      {children}
    </Link>
  );
}
